import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Subscription Status Component
 *
 * Displays current subscription status with appropriate styling and actions
 * Used in dashboards and settings pages
 */

const SubscriptionStatus = ({ subscriptionData, userType = "business" }) => {
  const navigate = useNavigate();

  if (!subscriptionData) {
    return null;
  }

  const {
    subscriptionStatus,
    subscriptionInterval,
    trialEndsAt,
    gracePeriodEndsAt,
    nextBillingDate,
    totalMonthlyAmount,
    totalAnnualAmount,
    goLiveDate,
    preTrialPaymentCollected,
  } = subscriptionData;

  // Calculate days remaining for trial or grace period
  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.floor((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const trialDaysRemaining = getDaysRemaining(trialEndsAt);
  const graceDaysRemaining = getDaysRemaining(gracePeriodEndsAt);
  const hasPartialTrialDayLeft =
    Boolean(trialEndsAt) &&
    new Date(trialEndsAt).getTime() > Date.now() &&
    trialDaysRemaining === 0;
  const trialTimeLabel = hasPartialTrialDayLeft
    ? "Less than 1 day left"
    : `${trialDaysRemaining} days left`;

  // Status configurations
  const statusConfig = {
    trialing: {
      color: "#3b82f6",
      bgColor: "#eff6ff",
      borderColor: "#bfdbfe",
      icon: "🚀",
      title: "Trial Period",
      badge: "TRIAL PERIOD",
    },
    active: {
      color: "#10b981",
      bgColor: "#f0fdf4",
      borderColor: "#bbf7d0",
      icon: "✅",
      title: "Active",
      badge: "ACTIVE",
    },
    past_due: {
      color: "#f59e0b",
      bgColor: "#fffbeb",
      borderColor: "#fde68a",
      icon: "⚠️",
      title: "Payment Failed",
      badge: "GRACE PERIOD",
    },
    canceled: {
      color: "#ef4444",
      bgColor: "#fef2f2",
      borderColor: "#fecaca",
      icon: "❌",
      title: "Canceled",
      badge: "CANCELED",
    },
    incomplete: {
      color: "#f59e0b",
      bgColor: "#fffbeb",
      borderColor: "#fde68a",
      icon: "⏳",
      title: "Setup Incomplete",
      badge: "ACTION REQUIRED",
    },
  };

  const config = statusConfig[subscriptionStatus] || statusConfig.active;

  // Calculate monthly cost display
  const monthlyAmount =
    subscriptionInterval === "year"
      ? Math.round(totalAnnualAmount / 12)
      : totalMonthlyAmount;

  return (
    <div
      style={{
        background: config.bgColor,
        border: `2px solid ${config.borderColor}`,
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "24px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "24px" }}>{config.icon}</span>
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "600",
                color: config.color,
              }}
            >
              {config.title}
            </h3>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: "13px",
                color: "#6b7280",
              }}
            >
              {subscriptionInterval === "year" ? "Annual Plan" : "Monthly Plan"}
            </p>
          </div>
        </div>
        <div
          style={{
            background: config.color,
            color: "white",
            padding: "6px 12px",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: "600",
            letterSpacing: "0.5px",
          }}
        >
          {config.badge}
        </div>
      </div>

      {/* Trial Period Info */}
      {subscriptionStatus === "trialing" && (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "14px", fontWeight: "500" }}>
              Trial Period
            </span>
            <span
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: config.color,
              }}
            >
              {trialTimeLabel}
            </span>
          </div>

          <div
            style={{
              background: "#f3f4f6",
              height: "8px",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                background: config.color,
                height: "100%",
                width: `${Math.max(hasPartialTrialDayLeft ? 10 : Math.max(10, (trialDaysRemaining / 7) * 100), 10)}%`,
                transition: "width 0.3s ease",
              }}
            />
          </div>

          {!preTrialPaymentCollected && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: "6px",
                padding: "12px",
                marginBottom: "12px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#92400e",
                  fontWeight: "500",
                }}
              >
                ⚠️ Payment Method Required
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: "12px",
                  color: "#78350f",
                }}
              >
                Add a payment method before your trial ends to continue service.
              </p>
            </div>
          )}

          <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
            {preTrialPaymentCollected
              ? `✅ Payment method saved. Billing starts ${new Date(goLiveDate).toLocaleDateString()}`
              : `Trial ends ${new Date(trialEndsAt).toLocaleDateString()}`}
          </p>
        </div>
      )}

      {/* Grace Period Info */}
      {subscriptionStatus === "past_due" && gracePeriodEndsAt && (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#dc2626",
            }}
          >
            ⚠️ Payment Failed - {graceDaysRemaining} days remaining
          </p>
          <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
            Please update your payment method. Access will be restricted after{" "}
            {new Date(gracePeriodEndsAt).toLocaleDateString()}.
          </p>
        </div>
      )}

      {/* Pricing Info */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "12px",
          }}
        >
          <p
            style={{
              margin: "0 0 4px",
              fontSize: "11px",
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {subscriptionInterval === "year"
              ? "Monthly Equivalent"
              : "Monthly Cost"}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "700",
              color: config.color,
            }}
          >
            ${monthlyAmount}
          </p>
        </div>

        {subscriptionInterval === "year" && (
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <p
              style={{
                margin: "0 0 4px",
                fontSize: "11px",
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Annual Total
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "700",
                color: config.color,
              }}
            >
              ${totalAnnualAmount}
            </p>
          </div>
        )}

        {nextBillingDate && subscriptionStatus === "active" && (
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <p
              style={{
                margin: "0 0 4px",
                fontSize: "11px",
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Next Billing
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              {new Date(nextBillingDate).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px" }}>
        {!preTrialPaymentCollected && subscriptionStatus === "trialing" && (
          <button
            onClick={() => navigate(`/${userType}/subscription/payment`)}
            style={{
              flex: 1,
              padding: "12px",
              background: config.color,
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            💳 Add Payment Method
          </button>
        )}

        {subscriptionStatus === "past_due" && (
          <button
            onClick={() => navigate(`/${userType}/subscription/payment`)}
            style={{
              flex: 1,
              padding: "12px",
              background: "#dc2626",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            🔄 Update Payment Method
          </button>
        )}

        <button
          onClick={(e) => {
            // Navigate to billing/invoices page instead of subscription page
            const routes = {
              business: "/dashboard/settings/invoices",
              freelancer: "/dashboard/freelancer/billing", // Future
            };
            const route = routes[userType] || "/dashboard/settings/invoices";
            try {
              navigate(route);
            } catch (error) {
              window.location.href = route;
            }
          }}
          style={{
            flex: 1,
            padding: "12px",
            background: "white",
            color: config.color,
            border: `2px solid ${config.borderColor}`,
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            position: "relative",
            zIndex: 10,
          }}
        >
          📄 View Billing & Invoices
        </button>
      </div>
    </div>
  );
};

export default SubscriptionStatus;
